import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Percent, Loader2, Plus, Trash2, Save, Edit2, X, Link2, Copy, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  code: '', type: 'percent', percent: 10, fixed_eur: 0,
  max_uses: '', expires_at: '', active: true, scope: 'any', note: '',
};

export default function AdminMarketingDiscounts() {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // One-time link manager state
  const [linkPanel, setLinkPanel] = useState(null); // { discount, links }
  const [linkLoading, setLinkLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Generator form (configurable max uses + per-user limit)
  const [linkForm, setLinkForm] = useState({ max_uses: 1, per_user_limit: false, note: '' });

  async function load() {
    setLoading(true);
    const r = await authFetch(`${API}/admin/marketing-discounts`);
    if (r.ok) { const d = await r.json(); setItems(d.items || []); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function openNew() { setEditing('new'); setForm(EMPTY); }
  function openEdit(it) {
    setEditing(it.id);
    setForm({
      code: it.code, type: it.type || 'percent',
      percent: it.percent || 0, fixed_eur: it.fixed_eur || 0,
      max_uses: it.max_uses ?? '', expires_at: it.expires_at || '',
      active: !!it.active, scope: it.scope || 'any', note: it.note || '',
    });
  }
  function close() { setEditing(null); setForm(EMPTY); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      percent: form.type === 'percent' ? Number(form.percent) : null,
      fixed_eur: form.type === 'fixed' ? Number(form.fixed_eur) : null,
      max_uses: form.max_uses === '' ? null : Number(form.max_uses),
      expires_at: form.expires_at || null,
      active: !!form.active,
      scope: form.scope,
      note: form.note,
    };
    try {
      const url = editing === 'new' ? `${API}/admin/marketing-discounts` : `${API}/admin/marketing-discounts/${editing}`;
      const method = editing === 'new' ? 'POST' : 'PUT';
      const r = await authFetch(url, { method, body: JSON.stringify(payload) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Greška');
      toast.success('Spremljeno');
      close(); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Obrisati ovaj kod?')) return;
    const r = await authFetch(`${API}/admin/marketing-discounts/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Obrisano'); load(); } else toast.error('Greška');
  }

  // ── One-time discount links ─────────────────────────────────────────
  async function openLinks(discount) {
    setLinkPanel({ discount, links: [] });
    setLinkForm({ max_uses: 1, per_user_limit: false, note: '' });
    setLinkLoading(true);
    try {
      const r = await authFetch(`${API}/admin/marketing-discounts/${discount.id}/links`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setLinkPanel({ discount, links: d.items || [] });
      else toast.error(d?.detail || 'Greška');
    } catch { toast.error('Greška'); }
    finally { setLinkLoading(false); }
  }

  async function generateLink() {
    if (!linkPanel?.discount) return;
    setGenerating(true);
    try {
      const payload = {
        max_uses: Math.max(1, parseInt(linkForm.max_uses) || 1),
        per_user_limit: !!linkForm.per_user_limit,
        note: linkForm.note || '',
      };
      const r = await authFetch(`${API}/admin/marketing-discounts/${linkPanel.discount.id}/links`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || 'Greška');
      const fullUrl = `${window.location.origin}${d.path}`;
      try { await navigator.clipboard.writeText(fullUrl); toast.success('Link kopiran u međuspremnik'); }
      catch { toast.success('Link generiran'); }
      await openLinks(linkPanel.discount);
    } catch (e) { toast.error(e.message); }
    finally { setGenerating(false); }
  }

  async function revokeLink(token) {
    if (!window.confirm('Opozvati ovaj link? Postaje neupotrebljiv.')) return;
    const r = await authFetch(`${API}/admin/marketing-discounts/links/${token}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Opozvano'); openLinks(linkPanel.discount); } else toast.error('Greška');
  }

  async function copyLink(path) {
    const fullUrl = `${window.location.origin}${path}`;
    try { await navigator.clipboard.writeText(fullUrl); toast.success('Kopirano'); }
    catch { toast.error('Kopiranje nije uspjelo — link: ' + fullUrl); }
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-marketing-discounts">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Percent className="text-emerald-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold">Marketing — popust kodovi</h1>
            <p className="text-sm text-zinc-400">Postotni / fiksni / besplatni (zero-price) kodovi za Oglašavanje i Sponzorirane članke.</p>
          </div>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-white text-sm font-semibold" data-testid="discount-new-btn">
          <Plus size={14} /> Novi kod
        </button>
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm" data-testid="discount-table">
            <thead className="bg-zinc-950 text-xs text-zinc-400 uppercase">
              <tr>
                <th className="text-left px-3 py-2.5">Kod</th>
                <th className="text-left px-3 py-2.5">Tip</th>
                <th className="text-left px-3 py-2.5">Vrijednost</th>
                <th className="text-left px-3 py-2.5">Iskorišteno</th>
                <th className="text-left px-3 py-2.5">Vrijedi do</th>
                <th className="text-left px-3 py-2.5">Polje</th>
                <th className="text-left px-3 py-2.5">Aktivan</th>
                <th className="px-3 py-2.5 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center px-4 py-6 text-zinc-500">Nema definiranih kodova.</td></tr>
              )}
              {items.map(it => (
                <tr key={it.id} className="border-t border-zinc-800" data-testid={`discount-row-${it.code}`}>
                  <td className="px-3 py-2 font-mono">{it.code}</td>
                  <td className="px-3 py-2">{it.type === 'percent' ? 'Postotak' : it.type === 'fixed' ? 'Fiksno' : 'Besplatno'}</td>
                  <td className="px-3 py-2 text-emerald-400 font-semibold">
                    {it.type === 'percent' && `${it.percent || 0}%`}
                    {it.type === 'fixed' && `−€ ${(it.fixed_eur || 0).toFixed(2)}`}
                    {it.type === 'zero' && '100% (FREE)'}
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{it.uses || 0}{it.max_uses ? ` / ${it.max_uses}` : ' / ∞'}</td>
                  <td className="px-3 py-2 text-zinc-400">{it.expires_at || '—'}</td>
                  <td className="px-3 py-2 text-zinc-400">{it.scope}</td>
                  <td className="px-3 py-2">{it.active ? <span className="text-emerald-400">DA</span> : <span className="text-zinc-500">NE</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openLinks(it)} title="Generiraj jednokratni link"
                              className="p-1.5 text-zinc-400 hover:text-emerald-400"
                              data-testid={`discount-links-${it.code}`}><Link2 size={14} /></button>
                      <button onClick={() => openEdit(it)} className="p-1.5 text-zinc-400 hover:text-amber-400" data-testid={`discount-edit-${it.code}`}><Edit2 size={14} /></button>
                      <button onClick={() => remove(it.id)} className="p-1.5 text-zinc-400 hover:text-red-400" data-testid={`discount-delete-${it.code}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {linkPanel && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setLinkPanel(null)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-2xl space-y-3"
               data-testid="discount-links-panel">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Link2 size={18} className="text-emerald-500" />
                  Linkovi za kod{' '}
                  <span className="font-mono text-emerald-400">{linkPanel.discount.code}</span>
                </h2>
                <p className="text-xs text-zinc-400 mt-1">
                  Odredite koliko puta se link smije naplatiti. Uključite <em>"1 po korisniku"</em> da svaki kupac (po e-mailu) može iskoristiti link samo jednom, čak i kad je broj korištenja veći.
                </p>
              </div>
              <button onClick={() => setLinkPanel(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="discount-links-close"><X size={18} /></button>
            </div>

            <div className="rounded-lg border border-zinc-800 p-3 bg-zinc-950/40 space-y-2" data-testid="discount-link-generator">
              <div className="grid sm:grid-cols-[1fr,1fr,auto] gap-2 items-end">
                <label className="block text-xs">
                  <span className="text-zinc-400">Maksimalan broj korištenja</span>
                  <input type="number" min={1} max={9999} value={linkForm.max_uses}
                         onChange={e => setLinkForm(f => ({ ...f, max_uses: e.target.value }))}
                         className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm"
                         data-testid="discount-link-max-uses" />
                </label>
                <label className="flex items-center gap-2 text-sm pb-2" data-testid="discount-link-per-user-label">
                  <input type="checkbox" checked={linkForm.per_user_limit}
                         onChange={e => setLinkForm(f => ({ ...f, per_user_limit: e.target.checked }))}
                         data-testid="discount-link-per-user-toggle" />
                  <span>Samo <span className="font-semibold">jednom po korisniku</span> (po e-mailu)</span>
                </label>
                <button onClick={generateLink} disabled={generating}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-white text-sm font-semibold disabled:opacity-50 self-end"
                        data-testid="discount-link-generate">
                  {generating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                  Generiraj link
                </button>
              </div>
              <p className="text-[11px] text-zinc-500">
                {Number(linkForm.max_uses) > 1
                  ? `Link je višekratan: do ${linkForm.max_uses} naplata.`
                  : 'Link je jednokratan: vrijedi samo do prve uspješne naplate.'}
                {linkForm.per_user_limit && ' Svaka e-mail adresa može iskoristiti najviše jednom.'}
              </p>
            </div>

            <div className="rounded-lg border border-zinc-800 overflow-hidden mt-3">
              <table className="w-full text-xs" data-testid="discount-links-table">
                <thead className="bg-zinc-950 text-zinc-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">URL</th>
                    <th className="text-left px-3 py-2 w-28">Korištenja</th>
                    <th className="text-left px-3 py-2 w-28">Status</th>
                    <th className="text-left px-3 py-2 w-36">Generiran</th>
                    <th className="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {linkLoading && (
                    <tr><td colSpan={5} className="text-center px-4 py-6 text-zinc-500"><Loader2 className="animate-spin inline" size={14} /></td></tr>
                  )}
                  {!linkLoading && linkPanel.links.length === 0 && (
                    <tr><td colSpan={5} className="text-center px-4 py-6 text-zinc-500">Nema generiranih linkova.</td></tr>
                  )}
                  {!linkLoading && linkPanel.links.map(l => {
                    const full = `${window.location.origin}${l.path}`;
                    const statusColor = l.status === 'active' ? 'text-emerald-400'
                      : l.status === 'used' ? 'text-zinc-500' : 'text-red-400';
                    return (
                      <tr key={l.token} className="border-t border-zinc-800" data-testid={`discount-link-row-${l.token}`}>
                        <td className="px-3 py-2 font-mono break-all">{full}</td>
                        <td className="px-3 py-2 text-zinc-300">
                          <div>{l.uses || 0} / {l.max_uses || 1}</div>
                          {l.per_user_limit && (
                            <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wide text-amber-400 font-semibold" data-testid={`discount-link-per-user-${l.token}`}>1 po korisniku</span>
                          )}
                        </td>
                        <td className={`px-3 py-2 uppercase font-semibold ${statusColor}`}>{l.status}</td>
                        <td className="px-3 py-2 text-zinc-400">{(l.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <button onClick={() => copyLink(l.path)}
                                    className="p-1.5 text-zinc-400 hover:text-emerald-400"
                                    title="Kopiraj link"
                                    data-testid={`discount-link-copy-${l.token}`}>
                              <Copy size={14} />
                            </button>
                            {l.status === 'active' && (
                              <button onClick={() => revokeLink(l.token)}
                                      className="p-1.5 text-zinc-400 hover:text-red-400"
                                      title="Opozovi"
                                      data-testid={`discount-link-revoke-${l.token}`}>
                                <Ban size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={close}>
          <form onSubmit={save} onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 w-full max-w-lg space-y-3" data-testid="discount-form">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold">{editing === 'new' ? 'Novi kod' : 'Uredi kod'}</h2>
              <button type="button" onClick={close} className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
            </div>
            <label className="block text-xs">
              <span className="text-zinc-400">Kod</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                     className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm font-mono uppercase" data-testid="discount-code" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="text-zinc-400">Tip</span>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-type">
                  <option value="percent">Postotni popust</option>
                  <option value="fixed">Fiksni iznos (EUR)</option>
                  <option value="zero">Zero-price (100% besplatno)</option>
                </select>
              </label>
              <label className="block text-xs">
                <span className="text-zinc-400">Polje primjene</span>
                <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-scope">
                  <option value="any">Sve kupnje</option>
                  <option value="advertising">Samo Oglašavanje</option>
                  <option value="sponsored">Samo Sponzorirani članak</option>
                </select>
              </label>
            </div>
            {form.type === 'percent' && (
              <label className="block text-xs">
                <span className="text-zinc-400">Postotak (1–100)</span>
                <input type="number" min={1} max={100} step={1} value={form.percent}
                       onChange={e => setForm(f => ({ ...f, percent: e.target.value }))}
                       className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-percent" />
              </label>
            )}
            {form.type === 'fixed' && (
              <label className="block text-xs">
                <span className="text-zinc-400">Iznos (EUR)</span>
                <input type="number" min={0.5} step={0.5} value={form.fixed_eur}
                       onChange={e => setForm(f => ({ ...f, fixed_eur: e.target.value }))}
                       className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-fixed" />
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="text-zinc-400">Max korištenja (prazno = neograničeno)</span>
                <input type="number" min={1} value={form.max_uses}
                       onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                       className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-max-uses" />
              </label>
              <label className="block text-xs">
                <span className="text-zinc-400">Vrijedi do (ISO datum)</span>
                <input type="date" value={form.expires_at}
                       onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                       className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" data-testid="discount-expires" />
              </label>
            </div>
            <label className="block text-xs">
              <span className="text-zinc-400">Bilješka (interno)</span>
              <textarea rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                        className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm" />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="discount-active" />
              Aktivan
            </label>
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-white text-sm font-semibold disabled:opacity-50" data-testid="discount-save">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Spremi
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
